// Eintraege einer Sammlung: der Stern-Zustand und die Gesamtpunktzahl auf
// dem Hauptscreen, dazu (Phase 4) die zeitlich sortierte Liste fuer
// "Letzte Kennzeichen".
//
// Eine Anfrage, zwei Sichten: der rohe Server-Array liegt unter
// ["entries", sammlungId] im Cache, useEntries() und
// useEintraegeSortiert() leiten daraus per useMemo ab, was sie brauchen.
// Optimistisch, weil der Stern sich sofort umschalten muss, nicht erst
// wenn die Antwort vom Server da ist. Schlaegt die Anfrage fehl, springt
// der Stern zurueck.

import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiFehler, api } from "./api.js";
import { OUTBOX_SCHLUESSEL, outboxEintragen } from "./outbox.js";
import type { Sammlung } from "./me.js";

export interface Eintrag {
  plateCodeId: number;
  firstSeenAt: string;
  pointsAwarded: number;
}

/** plateCodeId -> pointsAwarded. Eine Map traegt Stern-Zustand und Punkte in einem. */
export type Eintragskarte = Map<number, number>;

/**
 * Die persoenliche Sammlung.
 *
 * NUR fuer das Spiegeln aus einer gemeinsamen Sammlung, und dafuer am
 * besten ueber useEintragSetzenMitSpiegel() weiter unten. Wer
 * anzeigen will, was der Nutzer gesammelt hat, nimmt aktiveSammlung(): die
 * Statistik hing hier und meldete null, waehrend der Hauptscreen daneben
 * die Funde der gemeinsamen Sammlung zeigte.
 */
export function persoenlicheSammlung(
  sammlungen: Sammlung[],
): Sammlung | undefined {
  return sammlungen.find((s) => s.kind === "personal");
}

export function punkteGesamt(karte: Eintragskarte): number {
  let summe = 0;
  for (const punkte of karte.values()) summe += punkte;
  return summe;
}

function schluessel(sammlungId: number) {
  return ["entries", sammlungId] as const;
}

function useEntriesRoh(sammlungId: number | undefined) {
  return useQuery({
    queryKey:
      sammlungId === undefined ? ["entries", "keine"] : schluessel(sammlungId),
    enabled: sammlungId !== undefined,
    queryFn: async (): Promise<Eintrag[]> => {
      const antwort = await api<{ eintraege: Eintrag[] }>(
        `/collections/${sammlungId}/entries`,
      );
      return antwort.eintraege;
    },
  });
}

export function useEntries(sammlungId: number | undefined) {
  const roh = useEntriesRoh(sammlungId);
  const karte = useMemo(
    () =>
      new Map((roh.data ?? []).map((e) => [e.plateCodeId, e.pointsAwarded])),
    [roh.data],
  );
  return { ...roh, data: karte };
}

/** plateCodeId -> vollstaendiger Eintrag, fuer "Gesehen am ..." (IMG_4894). */
export function useEintragDetails(sammlungId: number | undefined) {
  const roh = useEntriesRoh(sammlungId);
  return useMemo(
    () => new Map((roh.data ?? []).map((e) => [e.plateCodeId, e])),
    [roh.data],
  );
}

/** Neueste zuerst, fuer "Letzte Kennzeichen". */
export function useEintraegeSortiert(sammlungId: number | undefined) {
  const roh = useEntriesRoh(sammlungId);
  const sortiert = useMemo(
    () =>
      [...(roh.data ?? [])].sort((a, b) =>
        b.firstSeenAt.localeCompare(a.firstSeenAt),
      ),
    [roh.data],
  );
  return { ...roh, data: sortiert };
}

export function useEintragSetzen(sammlungId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      plateCodeId,
      punkte,
    }: {
      plateCodeId: number;
      punkte: number;
    }) => {
      const clientUuid = crypto.randomUUID();
      try {
        return await api<Eintrag>(`/collections/${sammlungId}/entries`, {
          method: "POST",
          body: { plateCodeId, clientUuid },
        });
      } catch (fehler) {
        // ApiFehler heisst, der Server hat geantwortet (z. B. 409 - schon
        // gesammelt): ein echter Fehler, der Stern springt zurueck. Alles
        // andere ist eine gescheiterte Anfrage (kein Netz) - dafuer gibt es
        // die Outbox statt eines Fehlers, der optimistische Stern bleibt.
        if (fehler instanceof ApiFehler) throw fehler;
        if (sammlungId !== undefined) {
          await outboxEintragen({
            clientUuid,
            collectionId: sammlungId,
            plateCodeId,
            punkte,
            createdAt: new Date().toISOString(),
          });
          void queryClient.invalidateQueries({ queryKey: OUTBOX_SCHLUESSEL });
        }
        return {
          plateCodeId,
          pointsAwarded: punkte,
          firstSeenAt: new Date().toISOString(),
        };
      }
    },
    onMutate: async ({ plateCodeId, punkte }) => {
      if (sammlungId === undefined) return;
      await queryClient.cancelQueries({ queryKey: schluessel(sammlungId) });
      const vorher = queryClient.getQueryData<Eintrag[]>(
        schluessel(sammlungId),
      );
      queryClient.setQueryData<Eintrag[]>(schluessel(sammlungId), (aktuell) => [
        ...(aktuell ?? []),
        {
          plateCodeId,
          pointsAwarded: punkte,
          firstSeenAt: new Date().toISOString(),
        },
      ]);
      return { vorher };
    },
    onError: (_fehler, _variablen, kontext) => {
      if (sammlungId !== undefined && kontext?.vorher) {
        queryClient.setQueryData(schluessel(sammlungId), kontext.vorher);
      }
    },
    onSuccess: (antwort, { plateCodeId }) => {
      // Der optimistische Eintrag hat ein geschaetztes firstSeenAt; nach
      // Erfolg durch den echten Server-Wert ersetzen (oder, aus der Outbox,
      // durch denselben geschaetzten Wert noch einmal), sonst stimmt die
      // Reihenfolge in "Letzte Kennzeichen" nicht exakt.
      if (sammlungId === undefined) return;
      queryClient.setQueryData<Eintrag[]>(schluessel(sammlungId), (aktuell) =>
        (aktuell ?? []).map((e) =>
          e.plateCodeId === plateCodeId ? antwort : e,
        ),
      );
    },
  });
}

export function useEintragEntfernen(sammlungId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (plateCodeId: number) =>
      api<void>(`/collections/${sammlungId}/entries/${plateCodeId}`, {
        method: "DELETE",
      }),
    onMutate: async (plateCodeId) => {
      if (sammlungId === undefined) return;
      await queryClient.cancelQueries({ queryKey: schluessel(sammlungId) });
      const vorher = queryClient.getQueryData<Eintrag[]>(
        schluessel(sammlungId),
      );
      queryClient.setQueryData<Eintrag[]>(schluessel(sammlungId), (aktuell) =>
        (aktuell ?? []).filter((e) => e.plateCodeId !== plateCodeId),
      );
      return { vorher };
    },
    onError: (_fehler, _plateCodeId, kontext) => {
      if (sammlungId !== undefined && kontext?.vorher) {
        queryClient.setQueryData(schluessel(sammlungId), kontext.vorher);
      }
    },
  });
}

/**
 * Funde aus einer anderen eigenen Sammlung uebernehmen (Statistik,
 * "Nur in der eigenen Sammlung").
 *
 * Nicht optimistisch: anders als der Stern geht es hier um mehrere hundert
 * Zeilen auf einmal, und der Server entscheidet, welche davon wirklich
 * fehlen. Nach der Antwort werden beide Sammlungen neu geholt.
 */
export function useEintraegeUebernehmen(zielSammlungId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (argumente: {
      vonCollectionId: number;
      plateCodeIds: number[];
    }) =>
      api<{ uebertragen: number; bereitsVorhanden: number }>(
        `/collections/${zielSammlungId}/entries/uebernehmen`,
        {
          method: "POST",
          body: argumente,
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["entries"] });
    },
  });
}

/**
 * Eintragen mit der Spiegelregel aus den Einstellungen.
 *
 * Wer in einer gemeinsamen Sammlung sammelt und "Zusaetzlich in die eigene
 * Sammlung eintragen" gesetzt hat, traegt mit einer Geste in beide ein.
 * Die Regel gehoert an EINE Stelle: sie stand vorher zweimal ausgeschrieben
 * im Hauptscreen und in der Statistik, waehrend Schnelleingabe und Karte sie
 * gar nicht kannten und still nur in die gemeinsame Sammlung schrieben.
 *
 * Betrifft nur das Hinzufuegen, nicht das Entfernen: wer aus Versehen aus
 * der gemeinsamen Sammlung entfernt, soll nicht auch den eigenen Fund
 * verlieren.
 */
/**
 * Wird beim Eintragen zusaetzlich in die eigene Sammlung geschrieben?
 *
 * Nur aus einer gemeinsamen Sammlung heraus: steht die eigene ohnehin auf
 * aktiv, waere die zweite Anfrage dieselbe Zeile.
 */
export function spiegeltInEigene(
  sammlung: Sammlung | undefined,
  persoenliche: Sammlung | undefined,
  spiegleInEigene: boolean | undefined,
): boolean {
  return (
    Boolean(spiegleInEigene) &&
    sammlung?.kind === "shared" &&
    persoenliche !== undefined
  );
}

export function useEintragSetzenMitSpiegel(
  sammlung: Sammlung | undefined,
  sammlungen: Sammlung[],
  spiegleInEigene: boolean | undefined,
) {
  const persoenliche = persoenlicheSammlung(sammlungen);
  const inSammlung = useEintragSetzen(sammlung?.id);
  // Ist die persoenliche Sammlung zugleich die aktive, teilen sich beide
  // Aufrufe denselben Query-Key; es geht dann keine zweite Anfrage raus.
  const inEigene = useEintragSetzen(persoenliche?.id);
  const spiegeln = spiegeltInEigene(sammlung, persoenliche, spiegleInEigene);

  // Bewusst ohne useCallback: die Funktion landet nur in Klick-Handlern,
  // nie in einer Abhaengigkeitsliste, und useCallback ueber zwei
  // Mutations-Objekte hinweg waere hier mehr Fussangel als Nutzen.
  function setzen(argumente: { plateCodeId: number; punkte: number }) {
    inSammlung.mutate(argumente);
    if (spiegeln) inEigene.mutate(argumente);
  }

  return { setzen, spiegeln, persoenliche, isPending: inSammlung.isPending };
}
